import { useReducer, useState } from 'react';
import { useSelect } from '@refinedev/core';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { FormField, FormCard } from '@/components/forms';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import type {
  ScheduleDay,
  ScheduleNotificationSettingsValue,
} from '@/components/schedule';
import {
  TranslationsForm,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import { ContentTagsField } from '@/components/shared';
import type { Course, CourseFormData } from '../types';

// ─── Constants ───────────────────────────────────────────────────────────────

type CourseType = 'ONLINE' | 'MEETING' | 'OFFLINE' | 'WEBINAR';

const COURSE_TYPE_OPTIONS: { value: CourseType; label: string; description: string }[] = [
  { value: 'ONLINE', label: 'Онлайн', description: 'Живые вебинары по расписанию' },
  { value: 'WEBINAR', label: 'Вебинар', description: 'Онлайн-вебинар (как онлайн-курс)' },
  { value: 'MEETING', label: 'Клуб', description: 'Клубное мероприятие с модулями' },
  { value: 'OFFLINE', label: 'Оффлайн', description: 'Очные занятия' },
];



const ONLINE_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Название курса', type: 'input', required: true },
  { name: 'shortDescription', label: 'Краткое описание', type: 'richtext' },
  { name: 'description', label: 'Полное описание', type: 'richtext' },
];

const MEETING_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Название встречи', type: 'input', required: true },
  { name: 'description', label: 'Программа', type: 'richtext', placeholder: 'Программа встречи...' },
  { name: 'location', label: 'Место проведения', type: 'input', placeholder: 'Например: Стамбул' },
  { name: 'venue', label: 'Отель / Площадка', type: 'input', placeholder: 'Например: Hilton Bomonti' },
];

function getTranslationFields(courseType: CourseType): TranslationFieldConfig[] {
  if (courseType === 'MEETING') {
    return MEETING_TRANSLATION_FIELDS;
  }
  return ONLINE_TRANSLATION_FIELDS;
}


// ─── Transliteration ─────────────────────────────────────────────────────────

const CYR: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo',
  ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
  ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function toSlug(text: string): string {
  return text.toLowerCase()
    .replaceAll(/[а-яё]/g, (c) => CYR[c] ?? c)
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .slice(0, 50);
}

// ─── Types & State ───────────────────────────────────────────────────────────

interface FileRecord {
  id: number;
  url: string;
}

export interface FormState {
  slug: string;
  thumbnail: FileRecord | null;
  bannerImage: FileRecord | null;
  pastImage: FileRecord | null;
  previewVideoUrl: string;
  instructorIds: string[];
  isPublished: boolean;
  durationMinutes: number | null;
  courseType: CourseType;
  startDate: Date | null;
  endDate: Date | null;
  /** Часы проведения в формате HH:MM (например '18:00'), null если не задано. */
  courseHoursFrom: string | null;
  courseHoursTo: string | null;
  accessDurationDays: number | null;
  schedule: ScheduleDay[];
  scheduleTimezone: string;
  scheduleNotificationSettings: ScheduleNotificationSettingsValue | null;
  translations: TranslationData;
  lang: LanguageCode;
  order: number;
  onAccessExpiredTriggerId: string | null;
  onCourseCompletedTriggerId: string | null;
  /** Языковая аудитория курса. [] — виден всем, ['ru'] — только ru, и т.д. */
  targetLanguages: string[];
}

type Action =
  | { type: 'SET'; field: keyof FormState; value: FormState[keyof FormState] }
  | { type: 'SET_TRANSLATIONS'; data: TranslationData; autoSlug: boolean };

function reducer(s: FormState, a: Action): FormState {
  switch (a.type) {
    case 'SET':
      return { ...s, [a.field]: a.value };
    case 'SET_TRANSLATIONS': {
      const next = { ...s, translations: a.data };
      if (a.autoSlug && !s.slug && a.data.ru?.title) {
        next.slug = toSlug(a.data.ru.title);
      }
      return next;
    }
    default:
      return s;
  }
}

/**
 * Извлекает instructorIds из курса.
 * Приоритет: instructors (many-to-many) > instructorId (legacy).
 */
function extractInstructorIds(course: Course): string[] {
  if (course.instructors && course.instructors.length > 0) {
    return course.instructors.map((i) => String(i.id));
  }
  if (course.instructorId !== null && course.instructorId !== undefined) {
    return [String(course.instructorId)];
  }
  return [];
}

export function initFormState(course?: Course): FormState {
  const base: FormState = {
    slug: '', thumbnail: null, bannerImage: null, pastImage: null, previewVideoUrl: '',
    instructorIds: [], isPublished: false,
    durationMinutes: null, courseType: 'ONLINE',
    startDate: null, endDate: null,
    courseHoursFrom: null, courseHoursTo: null,
    accessDurationDays: null,
    schedule: [], scheduleTimezone: '', scheduleNotificationSettings: null,
    translations: createEmptyTranslationData(),
    lang: 'ru', order: 0,
    onAccessExpiredTriggerId: null, onCourseCompletedTriggerId: null,
    targetLanguages: [],
  };
  if (!course) {return base;}

  const translations = createEmptyTranslationData();
  for (const t of course.translations ?? []) {
    translations[t.languageCode] = {
      title: t.title, description: t.description ?? '', shortDescription: t.shortDescription ?? '',
      location: t.location ?? '', venue: t.venue ?? '',
    };
  }

  const schedule: ScheduleDay[] = Array.isArray(course.schedule)
    ? course.schedule.map((item) => ({
      date: item.date,
      title: item.title,
      description: item.description,
      items: item.items ?? [],
    }))
    : [];

  return {
    ...base, slug: course.slug,
    thumbnail: course.thumbnailId && course.thumbnailUrl
      ? { id: course.thumbnailId, url: course.thumbnailUrl }
      : course.thumbnailUrl ? { id: 0, url: course.thumbnailUrl } : null,
    bannerImage: course.bannerImageId && course.bannerImageUrl
      ? { id: course.bannerImageId, url: course.bannerImageUrl }
      : null,
    pastImage: course.pastImageId && course.pastImageUrl
      ? { id: course.pastImageId, url: course.pastImageUrl }
      : null,
    previewVideoUrl: course.previewVideoUrl ?? '',
    instructorIds: extractInstructorIds(course),
    isPublished: course.isPublished, durationMinutes: course.durationMinutes,
    courseType: course.courseType, startDate: course.startDate ? new Date(course.startDate) : null,
    endDate: course.endDate ? new Date(course.endDate) : null,
    courseHoursFrom: course.courseHoursFrom ?? null,
    courseHoursTo: course.courseHoursTo ?? null,
    accessDurationDays: course.accessDurationDays, schedule,
    scheduleTimezone: course.scheduleTimezone ?? '',
    scheduleNotificationSettings: course.scheduleNotificationSettings ?? null,
    translations,
    order: course.order,
    onAccessExpiredTriggerId: course.onAccessExpiredTriggerId !== null && course.onAccessExpiredTriggerId !== undefined
      ? String(course.onAccessExpiredTriggerId) : null,
    onCourseCompletedTriggerId: course.onCourseCompletedTriggerId !== null && course.onCourseCompletedTriggerId !== undefined
      ? String(course.onCourseCompletedTriggerId) : null,
    targetLanguages: course.targetLanguages ?? [],
  };
}

/**
 * Для создания — все поля.
 */
export function buildCreateFormData(s: FormState): CourseFormData {
  return {
    slug: toSlug(s.slug), durationMinutes: s.durationMinutes,
    thumbnailUrl: s.thumbnail?.url ?? null,
    thumbnailId: s.thumbnail?.id ?? null,
    bannerImageId: s.bannerImage?.id ?? null,
    pastImageId: s.pastImage?.id ?? null,
    previewVideoUrl: s.previewVideoUrl || null,
    isPublished: s.isPublished,
    instructorIds: s.instructorIds.map(Number),
    courseType: s.courseType, startDate: s.startDate?.toISOString() ?? null,
    endDate: s.endDate?.toISOString() ?? null,
    courseHoursFrom: s.courseHoursFrom,
    courseHoursTo: s.courseHoursTo,
    accessDurationDays: s.accessDurationDays,
    order: s.order,
    onAccessExpiredTriggerId: s.onAccessExpiredTriggerId ? Number(s.onAccessExpiredTriggerId) : null,
    onCourseCompletedTriggerId: s.onCourseCompletedTriggerId ? Number(s.onCourseCompletedTriggerId) : null,
    targetLanguages: s.targetLanguages,
    translations: Object.entries(s.translations)
      .filter(([, f]) => f.title)
      .map(([code, f]) => ({
        languageCode: code, title: f.title,
        description: f.description || null, shortDescription: f.shortDescription || null,
        location: f.location || null, venue: f.venue || null,
      })),
  };
}

/**
 * Для обновления — только изменённые поля (partial update).
 * Сравнивает текущий state с initial и отправляет только diff.
 * Translations отправляются всегда (они upsert-ятся на бэкенде).
 */
export function buildUpdateFormData(s: FormState, initial: FormState): Partial<CourseFormData> {
  const data: Partial<CourseFormData> = {};

  if (toSlug(s.slug) !== toSlug(initial.slug)) {
    data.slug = toSlug(s.slug);
  }
  if (s.durationMinutes !== initial.durationMinutes) {
    data.durationMinutes = s.durationMinutes;
  }
  if (s.thumbnail?.id !== initial.thumbnail?.id) {
    data.thumbnailUrl = s.thumbnail?.url ?? null;
    data.thumbnailId = s.thumbnail?.id ?? null;
  }
  if (s.bannerImage?.id !== initial.bannerImage?.id) {
    data.bannerImageId = s.bannerImage?.id ?? null;
  }
  if (s.pastImage?.id !== initial.pastImage?.id) {
    data.pastImageId = s.pastImage?.id ?? null;
  }
  if ((s.previewVideoUrl || null) !== (initial.previewVideoUrl || null)) {
    data.previewVideoUrl = s.previewVideoUrl || null;
  }
  if (s.isPublished !== initial.isPublished) {
    data.isPublished = s.isPublished;
  }
  if (JSON.stringify(s.instructorIds.map(Number).sort()) !== JSON.stringify(initial.instructorIds.map(Number).sort())) {
    data.instructorIds = s.instructorIds.map(Number);
  }
  if (s.courseType !== initial.courseType) {
    data.courseType = s.courseType;
  }
  if (s.startDate?.toISOString() !== initial.startDate?.toISOString()) {
    data.startDate = s.startDate?.toISOString() ?? null;
  }
  if (s.endDate?.toISOString() !== initial.endDate?.toISOString()) {
    data.endDate = s.endDate?.toISOString() ?? null;
  }
  if (s.courseHoursFrom !== initial.courseHoursFrom) {
    data.courseHoursFrom = s.courseHoursFrom;
  }
  if (s.courseHoursTo !== initial.courseHoursTo) {
    data.courseHoursTo = s.courseHoursTo;
  }
  if (s.accessDurationDays !== initial.accessDurationDays) {
    data.accessDurationDays = s.accessDurationDays;
  }
  if (s.order !== initial.order) {
    data.order = s.order;
  }
  if (s.onAccessExpiredTriggerId !== initial.onAccessExpiredTriggerId) {
    data.onAccessExpiredTriggerId = s.onAccessExpiredTriggerId ? Number(s.onAccessExpiredTriggerId) : null;
  }
  if (s.onCourseCompletedTriggerId !== initial.onCourseCompletedTriggerId) {
    data.onCourseCompletedTriggerId = s.onCourseCompletedTriggerId ? Number(s.onCourseCompletedTriggerId) : null;
  }
  if (JSON.stringify([...s.targetLanguages].sort()) !== JSON.stringify([...initial.targetLanguages].sort())) {
    data.targetLanguages = s.targetLanguages;
  }

  // Translations — всегда отправляем (upsert на бэкенде)
  data.translations = Object.entries(s.translations)
    .filter(([, f]) => f.title)
    .map(([code, f]) => ({
      languageCode: code, title: f.title,
      description: f.description || null, shortDescription: f.shortDescription || null,
      location: f.location || null, venue: f.venue || null,
    }));

  return data;
}

// ─── Trigger Select ──────────────────────────────────────────────────────────

interface TriggerOption {
  id: string;
  name: string;
}

function TriggerSelect({ value, onChange, label }: {
  value: string | null;
  onChange: (v: string | null) => void;
  label: string;
}) {
  const { options, query } = useSelect<TriggerOption>({
    resource: 'triggers',
    optionLabel: 'name',
    optionValue: 'id',
  });

  return (
    <FormField label={label} name="trigger">
      <Select
        value={value ?? 'none'}
        onValueChange={(v) => onChange(v === 'none' ? null : v)}
        disabled={query.isLoading}
      >
        <SelectTrigger><SelectValue placeholder="Не выбран" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Не выбран</SelectItem>
          {options.map((o) => (
            <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  );
}

// ─── Form Fields Component ──────────────────────────────────────────────────

export interface CourseFormFieldsProps {
  isEdit: boolean;
  loading: boolean;
  initial: FormState;
  instrOpts: { label: string; value: string | number }[];
  languages: TranslationLanguage[];
  courseId?: string;
  renderHeader?: (state: FormState, setField: <K extends keyof FormState>(field: K, value: FormState[K]) => void) => React.ReactNode;
  renderFooter?: (submit: () => void, loading: boolean) => React.ReactNode;
  onSubmit: (data: Partial<CourseFormData>) => Promise<void>;
}

export function CourseFormFields({
  isEdit, loading, initial, instrOpts, languages, courseId,
  renderHeader, renderFooter, onSubmit,
}: CourseFormFieldsProps) {
  const [s, d] = useReducer(reducer, initial);
  const [_advancedOpen, _setAdvancedOpen] = useState(false);

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    d({ type: 'SET', field, value: value as FormState[keyof FormState] });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!s.slug.trim()) {
      errs.slug = 'Slug обязателен';
    }
    const hasTitle = Object.values(s.translations).some((t) => t.title.trim());
    if (!hasTitle) {
      errs.title = 'Название курса обязательно';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) { return; }
    const data = isEdit ? buildUpdateFormData(s, initial) : buildCreateFormData(s);
    await onSubmit(data);
  };

  const typeInfo = COURSE_TYPE_OPTIONS.find((o) => o.value === s.courseType);

  return (
    <div className="space-y-6 pt-6">
      {renderHeader?.(s, set)}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* ═══ Main column (2/3) ═══ */}
        <div className="space-y-6 lg:col-span-2">
          {/* Контент */}
          <FormCard title="Контент">
            {languages.length > 0 ? (
              <TranslationsForm
                fields={getTranslationFields(s.courseType)}
                value={s.translations}
                onChange={(data) => {
                  d({ type: 'SET_TRANSLATIONS', data, autoSlug: !isEdit });
                  if (errors.title) { setErrors((prev) => { const { title: _, ...rest } = prev; return rest; }); }
                }}
                languages={languages}
                activeLanguage={s.lang}
                onLanguageChange={(c) => set('lang', c)}
              />
            ) : (
              <FormField label="Название курса" name="title" required>
                <Input
                  value={s.translations.ru?.title ?? ''}
                  onChange={(e) => {
                    const data = { ...s.translations, ru: { ...s.translations.ru, title: e.target.value } };
                    d({ type: 'SET_TRANSLATIONS', data, autoSlug: !isEdit });
                    if (errors.title) { setErrors((prev) => { const { title: _, ...rest } = prev; return rest; }); }
                  }}
                  placeholder="Введите название курса"
                  className="text-lg"
                />
              </FormField>
            )}
            {errors.title && (
              <p className="text-sm text-destructive mt-1">{errors.title}</p>
            )}
          </FormCard>

          {/* Медиа */}
          <FormCard title="Обложка курса" description="Изображение отображается в каталоге и на странице курса">
            <FormField label="Обложка (3:4)" name="thumbnail" hint="Карточка курса в каталоге, 3:4">
              <FileImageUpload
                value={s.thumbnail}
                onChange={(file) => set('thumbnail', file)}
                aspectRatio="3/4"
                placeholder="Рекомендуемое соотношение сторон 3:4"
              />
            </FormField>
            <FormField label="Баннер (16:9)" name="bannerImage" hint="Отображается на странице курса">
              <FileImageUpload
                value={s.bannerImage}
                onChange={(file) => set('bannerImage', file)}
                aspectRatio="16/9"
                placeholder="Рекомендуемое соотношение сторон 16:9"
              />
            </FormField>
            {s.courseType === 'MEETING' && (
              <FormField label="Обложка прошедшего (3:4)" name="pastImage" hint="Карточка завершённой встречи, 3:4">
                <FileImageUpload
                  value={s.pastImage}
                  onChange={(file) => set('pastImage', file)}
                  aspectRatio="3/4"
                  placeholder="Рекомендуемое соотношение сторон 3:4"
                />
              </FormField>
            )}
          </FormCard>

          <FormCard title="Даты проведения" description="Когда проходит курс. Необязательно для любого формата.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Дата начала" name="startDate">
                <DateTimePicker value={s.startDate} onChange={(date) => set('startDate', date)} placeholder="Начало" />
              </FormField>
              <FormField label="Дата окончания" name="endDate">
                <DateTimePicker value={s.endDate} onChange={(date) => set('endDate', date)} placeholder="Окончание" />
              </FormField>
              <FormField label="С" name="courseHoursFrom">
                <Input
                  type="time"
                  value={s.courseHoursFrom ?? ''}
                  onChange={(e) => set('courseHoursFrom', e.target.value || null)}
                />
              </FormField>
              <FormField label="До" name="courseHoursTo">
                <Input
                  type="time"
                  value={s.courseHoursTo ?? ''}
                  onChange={(e) => set('courseHoursTo', e.target.value || null)}
                />
              </FormField>
            </div>
          </FormCard>
        </div>

        {/* ═══ Sidebar (1/3) ═══ */}
        <div className="space-y-6">
          {/* Настройки */}
          <FormCard title="Настройки">
            <div className="space-y-4">
              <FormField label="Формат" name="courseType">
                <Select value={s.courseType} onValueChange={(v) => set('courseType', v as CourseType)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COURSE_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {typeInfo && (
                  <p className="text-xs text-muted-foreground mt-1">{typeInfo.description}</p>
                )}
              </FormField>

              <FormField label="Преподаватели" name="instructorIds">
                <MultiSelect
                  options={instrOpts.map((o) => ({ label: o.label, value: String(o.value) }))}
                  value={s.instructorIds}
                  onChange={(ids) => set('instructorIds', ids)}
                  placeholder="Не выбраны"
                  searchPlaceholder="Поиск преподавателя..."
                  emptyMessage="Преподаватели не найдены"
                />
              </FormField>

              <FormField label="Длительность (мин)" name="durationMinutes">
                <Input
                  type="number" value={s.durationMinutes ?? ''}
                  onChange={(e) => set('durationMinutes', e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder="120"
                />
              </FormField>

              <FormField label="Статус" name="isPublished">
                <Select
                  value={s.isPublished ? 'published' : 'draft'}
                  onValueChange={(v) => set('isPublished', v === 'published')}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Опубликован</SelectItem>
                    <SelectItem value="draft">Черновик</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </FormCard>

          {/* Доступ */}
          <FormCard title="Доступ">
            <div className="space-y-4">
              {(s.courseType === 'ONLINE' || s.courseType === 'WEBINAR') && (
                <FormField label="Срок доступа (дни)" name="accessDurationDays" hint="Сколько дней пользователь имеет доступ к курсу после покупки. Оставьте пустым для бессрочного доступа.">
                  <Input
                    type="number" min={1} value={s.accessDurationDays ?? ''}
                    onChange={(e) => set('accessDurationDays', e.target.value ? parseInt(e.target.value, 10) : null)}
                    placeholder="∞ Бессрочно"
                  />
                </FormField>
              )}
              <FormField label="Slug (URL)" name="slug" required hint="Уникальный идентификатор для URL. Только строчные буквы, цифры и дефисы.">
                <Input
                  value={s.slug}
                  onChange={(e) => {
                    const sanitized = e.target.value
                      .toLowerCase()
                      .replaceAll(/[а-яё]/g, (c) => CYR[c] ?? c)
                      .replaceAll(/[^a-z0-9-]/g, '-')
                      .replaceAll(/-{2,}/g, '-');
                    set('slug', sanitized);
                    if (errors.slug) { setErrors((prev) => { const { slug: _, ...rest } = prev; return rest; }); }
                  }}
                  placeholder="my-course"
                  className={errors.slug ? 'border-destructive' : ''}
                />
                {errors.slug && (
                  <p className="text-sm text-destructive mt-1">{errors.slug}</p>
                )}
              </FormField>
            </div>
          </FormCard>

          {/* Triggers */}
          <FormCard title="Триггеры">
            <div className="space-y-4">
              <TriggerSelect
                label="При истечении доступа"
                value={s.onAccessExpiredTriggerId}
                onChange={(v) => set('onAccessExpiredTriggerId', v)}
              />
              <TriggerSelect
                label="При завершении курса"
                value={s.onCourseCompletedTriggerId}
                onChange={(v) => set('onCourseCompletedTriggerId', v)}
              />
            </div>
          </FormCard>

          {/* Аудитория: язык + теги — единый блок видимости */}
          <FormCard title="Аудитория">
            <div className="space-y-4">
              <FormField
                label="Видимость по языкам аудитории"
                name="targetLanguages"
                hint="Пустое = виден всем. Выберите языки — курс будет показан только пользователям с этими языками."
              >
                <MultiSelect
                  options={languages.map((l) => ({ label: l.name, value: l.code }))}
                  value={s.targetLanguages}
                  onChange={(value) => set('targetLanguages', value)}
                  placeholder="Виден всем"
                  searchPlaceholder="Поиск языка..."
                  emptyMessage="Языки не найдены"
                />
              </FormField>

              {isEdit && courseId ? (
                <ContentTagsField
                  entityType="COURSE"
                  entityId={Number(courseId)}
                  label="Теги (видимость)"
                  description="Видят пользователи с пересечением тегов. Пусто = виден всем."
                />
              ) : null}
            </div>
          </FormCard>
        </div>
      </div>

      {renderFooter?.(() => { void submit(); }, loading)}
    </div>
  );
}
